import { validate } from "uuid";
import { displayCategoryName, displayName, getEvidenceName } from "~/common/functions";
import type { OrderBy, RecordsBoolExp, RecordsOrderBy } from "~/common/types/graphql";
import {
  type EffectiveRecordPropertySchemasByCategory,
  requireEffectiveRecordSchemas,
} from "~/features/recordDetails/utils/effective-property-schemas";
import { geoItemDisplayName, geoItemLinkHeading, geoItemPlaceName } from "~/features/recordGeoItems/utils/geoItemLabel";
import type { GetRelationDetailGeoItemsQuery, GetRelationsQueryVariables } from "~/features/relations/gqls";
import type {
  LineageEdge,
  LineageEdgeType,
  LineageNode,
  RelatedEntityLink,
  RelationCategoryChip,
  RelationChangeRequestRow,
  RelationDetailRaw,
  RelationDetailUI,
  RelationEvidenceRow,
  RelationGeoItemRow,
  RelationLineage,
  RelationListFilters,
  RelationPropertyGroup,
  RelationRaw,
  RelationRow,
  RelationSortOrder,
  SortDirection,
} from "~/features/relations/types";
import type { Locale } from "~/i18n/resolveLocale";
import { parseGdtfRange } from "~/utils/gdtfRange";

const dedupeById = <T extends { id: string }>(items: readonly T[]): T[] => {
  const map = new Map<string, T>();
  for (const item of items) if (!map.has(item.id)) map.set(item.id, item);
  return [...map.values()];
};

const NOT_YET_APPLIED = new Set(["open", "approved"]);

const formatDateRange = (date?: string | null, endDate?: string | null): string => {
  if (!date && !endDate) return "";
  if (date && endDate) return `${date} ~ ${endDate}`;
  return date ?? endDate ?? "";
};

type CategoryShape = { id: string; name: string; i18nLabel: unknown };

const toCategoryChip = (category: CategoryShape, locale: Locale): RelationCategoryChip => ({
  id: category.id,
  label: displayCategoryName(
    { name: category.name, i18nLabel: category.i18nLabel as Record<string, unknown> | null },
    locale,
  ),
});

const toCategoryChips = (
  metadataCategories: ReadonlyArray<{ category: CategoryShape }>,
  locale: Locale,
): RelationCategoryChip[] => metadataCategories.map((rc) => toCategoryChip(rc.category, locale));

interface RelatedEntityShape {
  id: string;
  name?: string | null;
  nameJa?: string | null;
  date?: string | null;
  recordMetadata: { recordMetadataCategories: ReadonlyArray<{ category: CategoryShape }> };
}

const toRelatedEntityLink = (
  entity: RelatedEntityShape | null | undefined,
  locale: Locale,
): RelatedEntityLink | null => {
  if (!entity) return null;
  return {
    id: entity.id,
    displayName: displayName({ name: entity.name, nameJa: entity.nameJa }, locale),
    date: entity.date ?? null,
    categories: toCategoryChips(entity.recordMetadata.recordMetadataCategories, locale),
  };
};

const toEntityLinks = (
  edges: ReadonlyArray<{ entity?: RelatedEntityShape | null }> | undefined,
  locale: Locale,
): RelatedEntityLink[] =>
  (edges ?? []).map((e) => toRelatedEntityLink(e.entity, locale)).filter((r): r is RelatedEntityLink => r !== null);

function toRelationRow(raw: RelationRaw, locale: Locale): RelationRow {
  return {
    id: raw.id,
    nameJa: raw.nameJa ?? "",
    dateRange: formatDateRange(raw.date, raw.endDate),
    updatedAt: raw.updatedAt,
    categories: toCategoryChips(raw.recordMetadata.recordMetadataCategories, locale),
    sources: toEntityLinks(raw.relationRecord?.sourceRecordEdges, locale),
    targets: toEntityLinks(raw.relationRecord?.targetRecordEdges, locale),
    isDeactivated: Boolean(raw.deactivatedAt),
  };
}

export function toRelationRowList(raws: readonly RelationRaw[], locale: Locale): RelationRow[] {
  return raws.map((raw) => toRelationRow(raw, locale));
}

type RelationGeoItemRead = Extract<
  GetRelationDetailGeoItemsQuery["cnutGetPublishedRecordGeoItemsByRecord"],
  { __typename: "RecordGeoItemsByRecordOutput" }
>["recordGeoItems"][number];

export const toRelationGeoItemRows = (
  recordGeoItems: readonly RelationGeoItemRead[],
  locale: Locale,
): RelationGeoItemRow[] =>
  recordGeoItems.map((gi) => ({
    id: gi.geoItemId,
    label: geoItemDisplayName(gi.geoItemId, geoItemPlaceName(gi.geoItem?.i18nLabel, locale)),
    heading: geoItemLinkHeading(gi.i18nLabel, locale),
    date: gi.date ?? "",
    endDate: gi.endDate ?? "",
    countryIds: gi.countryCode ? [gi.countryCode] : [],
    deactivatedAt: gi.deactivatedAt ?? null,
    isDeactivated: Boolean(gi.deactivatedAt),
  }));

type MetadataCategory = RelationDetailRaw["recordMetadata"]["recordMetadataCategories"][number];

/** Property tables use the same effective schema contract as category detail. */
const toPropertyGroups = (
  metadataCategories: readonly MetadataCategory[],
  properties: Record<string, unknown>,
  locale: Locale,
  effectiveSchemas: EffectiveRecordPropertySchemasByCategory,
): RelationPropertyGroup[] =>
  metadataCategories.map((rc) => {
    const schemas = requireEffectiveRecordSchemas(effectiveSchemas, rc.category.id);
    return {
      categoryId: rc.category.id,
      categoryLabel: toCategoryChip(rc.category, locale).label,
      rows: schemas.map((schema) => ({
        position: schema.position,
        name: schema.metadataKey,
        value: properties[schema.metadataKey],
        propertyTypeId: schema.type,
        selectOptions: schema.selectOptions,
      })),
    };
  });

/** Properties not described by any category's schema (rendered as a raw JsonTable). */
const toOtherProperties = (
  metadataCategories: readonly MetadataCategory[],
  properties: Record<string, unknown>,
  effectiveSchemas: EffectiveRecordPropertySchemasByCategory,
): Record<string, unknown> => {
  const known = new Set(
    metadataCategories.flatMap((rc) =>
      requireEffectiveRecordSchemas(effectiveSchemas, rc.category.id).map((schema) => schema.metadataKey),
    ),
  );
  return Object.fromEntries(Object.entries(properties).filter(([key]) => !known.has(key)));
};

export function toRelationDetailUI(
  raw: RelationDetailRaw,
  locale: Locale,
  recordGeoItems: readonly RelationGeoItemRead[] = [],
  effectiveSchemas: EffectiveRecordPropertySchemasByCategory,
): RelationDetailUI {
  const properties = (raw.properties ?? {}) as Record<string, unknown>;
  return {
    id: raw.id,
    displayName: displayName({ name: null, nameJa: raw.nameJa }, locale),
    dateRange: formatDateRange(raw.date, raw.endDate),
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
    deactivatedAt: raw.deactivatedAt ?? null,
    isDeactivated: Boolean(raw.deactivatedAt),
    categories: toCategoryChips(raw.recordMetadata.recordMetadataCategories, locale),
    propertyGroups: toPropertyGroups(raw.recordMetadata.recordMetadataCategories, properties, locale, effectiveSchemas),
    otherProperties: toOtherProperties(raw.recordMetadata.recordMetadataCategories, properties, effectiveSchemas),
    metadata: raw.recordMetadata,
    geoItems: toRelationGeoItemRows(recordGeoItems, locale),
    sources: toEntityLinks(raw.relationRecord?.sourceRecordEdges, locale),
    targets: toEntityLinks(raw.relationRecord?.targetRecordEdges, locale),
  };
}

/**
 * Build the dateJdn/endDateJdn overlap filter for a GDTF range.
 * Ported verbatim from V1 `~/models/records/searchProps` (pure logic).
 */
const buildGdtfRangeWhere = (gdtfFrom?: string, gdtfTo?: string): RecordsBoolExp[] => {
  const parsed = parseGdtfRange(gdtfFrom, gdtfTo);
  if (!parsed.hasFilter || parsed.inverted) return [];

  const conditions: RecordsBoolExp[] = [{ dateJdn: { _isNull: false } }];
  if (parsed.toJdn != null) {
    conditions.push({ dateJdn: { _lte: parsed.toJdn } });
  }
  if (parsed.fromJdn != null) {
    conditions.push({
      _or: [
        { endDateJdn: { _gte: parsed.fromJdn } },
        { _and: [{ endDateJdn: { _isNull: true } }, { dateJdn: { _gte: parsed.fromJdn } }] },
      ],
    });
  }
  return conditions;
};

/** Translate the UI filter shape into a Hasura `RecordsBoolExp`. Ported from V1. */
export function buildRelationsWhere(filters: RelationListFilters): RecordsBoolExp {
  const andList: RecordsBoolExp[] = [];

  if (filters.idOrName) {
    andList.push(
      validate(filters.idOrName) ? { id: { _eq: filters.idOrName } } : { name: { _like: `%${filters.idOrName}%` } },
    );
  }
  if (filters.referenceIds.length > 0) {
    andList.push({
      changeRequestItemRecords: {
        changeRequestItem: {
          changeRequest: {
            evidenceChangeRequests: {
              evidence: { referenceId: { _in: filters.referenceIds } },
            },
          },
        },
      },
    });
  }
  if (filters.categoryIds.length > 0) {
    andList.push({
      recordMetadata: { recordMetadataCategories: { categoryId: { _in: filters.categoryIds } } },
    });
  }
  if (filters.excludeDeactivated) {
    andList.push({ deactivatedAt: { _isNull: true } });
  }
  andList.push(...buildGdtfRangeWhere(filters.gdtfFrom, filters.gdtfTo));

  return { _and: andList };
}

const orderValue = (direction: SortDirection): OrderBy => (direction === "asc" ? "ASC" : "DESC");

/** Build a `RecordsOrderBy` from the sort column + direction. */
function buildRelationsOrderBy(sortOrder: RelationSortOrder, direction: SortDirection): RecordsOrderBy {
  return { [sortOrder]: orderValue(direction) };
}

/** Assemble the full `GetRelations` query variables for a page of the list. */
export function buildRelationsVariables(args: {
  tenantId: string;
  filters: RelationListFilters;
  sortOrder: RelationSortOrder;
  direction: SortDirection;
  pageIndex: number;
  pageSize: number;
}): GetRelationsQueryVariables {
  const { tenantId, filters, sortOrder, direction, pageIndex, pageSize } = args;
  return {
    tenantId,
    limit: pageSize,
    offset: pageIndex * pageSize,
    where: {
      ...buildRelationsWhere(filters),
      // Restrict to relation records (exclude entities) via an inner-join style filter.
      relationRecord: {},
    },
    order_by: buildRelationsOrderBy(sortOrder, direction),
  };
}

// ── Detail: change requests / evidence / lineage ──────────────────────────────

const deriveChangeRequestItems = (raw: RelationDetailRaw) =>
  raw.changeRequestItemRecords
    .map((r) => r.changeRequestItem)
    .filter((ci): ci is NonNullable<typeof ci> => Boolean(ci));

const userName = (user: { displayName?: string | null; email?: string | null } | null | undefined): string =>
  user?.displayName || user?.email || "";

/** Change-request items for the detail Change Requests tab (all items, newest first). */
export function toRelationChangeRequestRows(raw: RelationDetailRaw): RelationChangeRequestRow[] {
  return deriveChangeRequestItems(raw).map((ci) => ({
    id: ci.id,
    createdAt: ci.createdAt,
    operationType: ci.operationType,
    properties: ci.properties,
    changeRequestId: ci.changeRequest.id,
    description: ci.changeRequest.description,
    userName: userName(ci.changeRequest.user),
    context: ci.changeRequest.context,
  }));
}

/** The non-rejected change requests reachable from this record (deduped). */
const liveChangeRequests = (raw: RelationDetailRaw) =>
  dedupeById(
    deriveChangeRequestItems(raw)
      .map((ci) => ci.changeRequest)
      .filter((cr) => cr.statusId !== "rejected"),
  );

/** One row per evidence, keyed on the first change request that names it: its own metadata, properties from the version that request pinned. */
const composedEvidences = (raw: RelationDetailRaw) =>
  dedupeById(
    liveChangeRequests(raw).flatMap((cr) =>
      cr.evidenceChangeRequests.map((ecr) => ({ ...ecr.evidence, properties: ecr.evidenceVersion.properties })),
    ),
  );

/** Evidence rows for the detail EvidencesBox (deduped). */
export function toRelationEvidenceRows(raw: RelationDetailRaw): RelationEvidenceRow[] {
  return composedEvidences(raw).map((ev) => {
    const props = (ev.properties ?? {}) as Record<string, unknown>;
    const ref = ev.reference;
    return {
      id: ev.id,
      date: typeof props.date === "string" ? props.date : "",
      name: getEvidenceName({ properties: props }),
      reference: ref ? { id: ref.id, shortCode: ref.shortCode, title: ref.title } : null,
      userName: userName(ev.user),
      updatedAt: ev.updatedAt ?? null,
    };
  });
}

/** Build the detail lineage graph: record → change requests → evidence → references. */
export function toRelationLineage(
  raw: RelationDetailRaw,
  labels: { record: string; changeRequest: string },
): RelationLineage {
  const changeRequests = liveChangeRequests(raw);
  const evidences = composedEvidences(raw);
  const references = dedupeById(evidences.map((e) => e.reference));

  const nodes: LineageNode[] = [
    { id: raw.id, type: "relation", label: labels.record, subLabel: raw.id },
    ...changeRequests.map(
      (cr): LineageNode => ({
        id: cr.id,
        type: "changeRequest",
        label: labels.changeRequest,
        subLabel: cr.id,
        muted: NOT_YET_APPLIED.has(cr.statusId),
      }),
    ),
    ...evidences.map(
      (ev): LineageNode => ({
        id: ev.id,
        type: "evidence",
        label: getEvidenceName({ properties: (ev.properties ?? {}) as Record<string, unknown> }),
        subLabel: ev.id,
      }),
    ),
    ...references.map(
      (rf): LineageNode => ({
        id: rf.id,
        type: "reference",
        label: rf.title || rf.shortCode || rf.id,
        subLabel: rf.id,
      }),
    ),
  ];

  const edges: LineageEdge[] = [];
  const push = (
    source: string,
    target: string,
    type: LineageEdgeType,
    extra: Pick<LineageEdge, "projectID" | "changeRequestJobID" | "evidenceJobID"> = {},
  ) => edges.push({ id: `${source}->${target}`, source, target, type, ...extra });

  // Applied change requests connect to the record (not-yet-applied are omitted).
  for (const cr of changeRequests) {
    if (!NOT_YET_APPLIED.has(cr.statusId)) push(cr.id, raw.id, "changeRequestToRecord");
  }
  // Evidence → change request (click-through opens the batch-change-requests job peek).
  for (const cr of changeRequests) {
    const bcrJob = cr.batchChangeRequestsJobRun?.batchChangeRequestsJob;
    for (const ecr of cr.evidenceChangeRequests) {
      push(ecr.evidence.id, cr.id, "evidenceToChangeRequest", {
        projectID: bcrJob?.dataImportProjectId ?? undefined,
        changeRequestJobID: bcrJob?.id ?? undefined,
      });
    }
  }
  // Reference → evidence (click-through opens the batch-evidences job peek).
  for (const ev of evidences) {
    push(ev.reference.id, ev.id, "referenceToEvidence", {
      projectID: ev.batchEvidencesJob?.dataImportProjectId ?? undefined,
      evidenceJobID: ev.batchEvidencesJob?.id ?? undefined,
    });
  }

  const edgeMap = new Map(edges.map((e) => [e.id, e]));
  return { nodes, edges: [...edgeMap.values()] };
}
